// Pick up where you left off (U3). Focus mode saves where it is in the round on the server (not
// in this browser), so a return from the feed, a reload or another device can offer: "You stopped
// at 4 of 9 yesterday afternoon. Pick up?" The feed's offer hands its choice to focus mode
// through `focusIntent`, so the question is asked once.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { History } from "lucide-react";
import type { FocusPosition } from "@shared/startEasily";
import { feedApi } from "@web/lib/feedApi";
import { useOwnerTimeZone } from "@web/lib/settingsApi";
import { useNow } from "@web/lib/useNow";
import { Button } from "@web/ui/Button";
import { shouldOfferPickUp, stoppedAtWords } from "@web/feed/startWords";

export type FocusIntent = "pickup" | "restart";

let pendingIntent: FocusIntent | null = null;

/** What the feed's offer chose; focus mode takes it once. */
export const focusIntent = {
  set(intent: FocusIntent) {
    pendingIntent = intent;
  },
  take(): FocusIntent | null {
    const intent = pendingIntent;
    pendingIntent = null;
    return intent;
  },
};

export const focusPositionKey = (feedId: string) => ["focusPosition", feedId] as const;

/** The saved position in the current round (read on arrival; never on a card switch). */
export function useFocusPosition(feedId: string | null) {
  return useQuery({
    queryKey: focusPositionKey(feedId ?? ""),
    queryFn: ({ signal }) => feedApi.focusPosition(feedId ?? "", signal),
    enabled: !!feedId,
    staleTime: 0,
  });
}

/** Forget the position of round `passNo` on the server (Start over, or the round is finished). */
export function useClearFocusPosition(feedId: string | null) {
  const queryClient = useQueryClient();
  return (passNo: number) => {
    if (!feedId) return;
    queryClient.setQueryData(focusPositionKey(feedId), { position: null });
    void feedApi.clearFocusPosition(feedId, passNo).catch(() => undefined);
  };
}

/** The question itself, used by the feed and by focus mode. */
export function PickUpPrompt({
  position,
  onPickUp,
  onStartOver,
  autoFocus = false,
}: {
  position: FocusPosition;
  onPickUp: () => void;
  onStartOver: () => void;
  autoFocus?: boolean;
}) {
  const now = useNow(60_000);
  const zone = useOwnerTimeZone();
  return (
    <section
      aria-label="Pick up where you left off"
      data-testid="pick-up"
      className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-paper px-4 py-3"
    >
      <History aria-hidden className="size-4 shrink-0 text-muted" />
      <p className="min-w-[12rem] flex-1 text-sm text-ink">
        {stoppedAtWords(position, now, zone)} Pick up?
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="primary" autoFocus={autoFocus} onClick={onPickUp}>
          Pick up
        </Button>
        <Button size="sm" variant="ghost" onClick={onStartOver}>
          Start over
        </Button>
      </div>
    </section>
  );
}

/** The feed's offer: shown when focus stopped part-way through this round a while ago. */
export function FeedPickUp({
  feedId,
  currentPass,
  hasOpenCards,
}: {
  feedId: string | null;
  currentPass: number | null;
  hasOpenCards: boolean;
}) {
  const navigate = useNavigate();
  const now = useNow(60_000);
  const position = useFocusPosition(feedId).data?.position ?? null;
  if (!hasOpenCards || !shouldOfferPickUp(position, currentPass, now)) return null;
  const go = (intent: FocusIntent) => {
    focusIntent.set(intent);
    void navigate({ to: "/focus" });
  };
  return (
    <div className="mx-4 mt-3 md:mx-5">
      <PickUpPrompt
        position={position}
        onPickUp={() => go("pickup")}
        onStartOver={() => go("restart")}
      />
    </div>
  );
}
